import { ObjectEvent } from 'ol/Object'
import BaseVectorLayer from 'ol/layer/BaseVector'
import VectorTileLayer from 'ol/layer/VectorTile'
import ImageWMS from 'ol/source/ImageWMS'
import TileWMS from 'ol/source/TileWMS'
import type BaseLayer from 'ol/layer/Base'
import type Collection from 'ol/Collection'
import type Layer from 'ol/layer/Layer'

import { parseWmsBbox, toViewExtent } from 'common/ol/olcs-extent'

/**
 * Per-layer hints telling ol-cesium how to project each OpenLayers layer into
 * the 3D scene.
 *
 * Applied from here rather than from the individual layer components, so the 2D
 * map is untouched and the 3D-specific knowledge lives in one place. Layers
 * mounted later — harvest rasters arrive asynchronously once a harvest is
 * picked — are caught by a listener on the layer collection.
 *
 * ol-cesium reads these from two different places, which is easy to get wrong:
 * `olcs_extent` and `altitudeMode` are read on the **layer**, `olcs_skip` on the
 * **source** (olcs/core.ts `sourceToImageryProvider`, olcs/FeatureConverter.ts).
 */

/** Drapes vector geometry onto the terrain instead of floating it at height 0. */
const ALTITUDE_MODE_PROPERTY = 'altitudeMode'
const CLAMP_TO_GROUND = 'clampToGround'

const OLCS_EXTENT_PROPERTY = 'olcs_extent'
const OLCS_SKIP_PROPERTY = 'olcs_skip'

/**
 * Layers whose WMS params are rewritten as the user zooms. ol-cesium snapshots
 * an ImageWMS's params when it builds the counterpart imagery provider and
 * never rebuilds it, so the server-side clustering these drive through
 * `updateParams` would freeze at whatever zoom 3D was entered on. A silently
 * stale overlay is worse than an absent one.
 */
const DYNAMIC_PARAM_LAYER_NAMES = [
  OUTSIDE_BOUNDARY_POINTS_LAYER_NAME,
  OUTSIDE_BOUNDARY_POINTS_GRID_LAYER_NAME,
].filter(Boolean)

const sourceOf = (layer: BaseLayer): unknown => {
  const getSource = (layer as Layer).getSource
  return typeof getSource === 'function'
    ? (layer as Layer).getSource()
    : undefined
}

const wmsLayerNamesOf = (source: unknown): string[] => {
  if (!(source instanceof ImageWMS) && !(source instanceof TileWMS)) return []
  const layers = source.getParams()?.LAYERS
  if (typeof layers !== 'string') return []
  return layers.split(',').map((name) => name.trim())
}

/**
 * Apply the hints a single layer needs. Idempotent, so it is safe to re-run
 * over a collection that has already been processed.
 */
export const applyLayerHints = (layer: BaseLayer): void => {
  const source = sourceOf(layer)

  if (
    source &&
    typeof (source as { set?: unknown }).set === 'function' &&
    wmsLayerNamesOf(source).some((name) =>
      DYNAMIC_PARAM_LAYER_NAMES.includes(name),
    )
  ) {
    ;(source as { set: (key: string, value: unknown) => void }).set(
      OLCS_SKIP_PROPERTY,
      true,
    )
    return
  }

  // Harvest rasters cover a single field. Without an extent ol-cesium hands the
  // imagery provider a whole-globe rectangle, and Cesium then requests GeoServer
  // tiles worldwide at every level. The harvest filter (CQL_FILTER, or HARVEST
  // through the GIS proxy) makes them all empty, but the requests are real and
  // they hit GeoWebCache.
  if (source instanceof TileWMS && !layer.get(OLCS_EXTENT_PROPERTY)) {
    const params = source.getParams() ?? {}
    const extent4326 = parseWmsBbox(
      params.BBOX as string | number[] | undefined,
    )
    if (extent4326) {
      // extentToRectangle converts this from the view projection, not degrees.
      layer.set(OLCS_EXTENT_PROPERTY, toViewExtent(extent4326))
    } else if (params.CQL_FILTER || params.HARVEST) {
      // A harvest-scoped layer we cannot fence. Leaving it unbounded means
      // Cesium requests GeoServer tiles across the whole planet at every level,
      // which exhausts the browser's connection pool and takes the rest of the
      // map down with it. An absent layer is the better failure.
      ;(source as { set: (key: string, value: unknown) => void }).set(
        OLCS_SKIP_PROPERTY,
        true,
      )
    }
  }

  // Vector features otherwise sit at ellipsoid height, floating above or buried
  // beneath the terrain depending on the local elevation. VectorTile layers are
  // excluded: ol-cesium rasterises those through the experimental
  // MVTImageryProvider and only when olcs_skip is explicitly false, so they are
  // not part of the 3D scene at all today.
  if (
    layer instanceof BaseVectorLayer &&
    !(layer instanceof VectorTileLayer) &&
    !layer.get(ALTITUDE_MODE_PROPERTY)
  ) {
    layer.set(ALTITUDE_MODE_PROPERTY, CLAMP_TO_GROUND)
  }
}

/**
 * Apply hints to every layer in a collection, and to any added afterwards.
 *
 * Takes the collection rather than the `ol/Map` so this can run without a DOM,
 * which is what makes it unit-testable.
 *
 * Returns an unsubscribe function.
 */
export const applyOlcsHints = (layers: Collection<BaseLayer>): (() => void) => {
  const sourceWatchers = new Map<BaseLayer, () => void>()

  // wms-layer swaps a layer's source in place when the swath parameter or
  // legend changes. ol-cesium builds its imagery provider around the source it
  // saw when the layer was added, and its own `change` handler only removes and
  // re-adds the *same* Cesium layer object, so the globe would keep painting
  // the previous raster.
  //
  // Its only true rebuild path is the `change:extent` listener, which destroys
  // the counterparts and re-runs synchronize(). Calling setExtent cannot reach
  // it here: these layers have no extent, and ol/Object only notifies when the
  // value actually changes, so setting undefined over undefined is silent.
  // Dispatching the event is therefore deliberate rather than a shortcut.
  const watchSource = (layer: BaseLayer) => {
    if (sourceWatchers.has(layer)) return
    const handler = () => {
      const extent = (layer as Layer).getExtent?.()
      layer.dispatchEvent(new ObjectEvent('change:extent', 'extent', extent))
    }
    layer.on('change:source' as never, handler as never)
    sourceWatchers.set(layer, () =>
      layer.un('change:source' as never, handler as never),
    )
  }

  const onAdd = (event: { element: BaseLayer }) => {
    applyLayerHints(event.element)
    watchSource(event.element)
  }

  const onRemove = (event: { element: BaseLayer }) => {
    sourceWatchers.get(event.element)?.()
    sourceWatchers.delete(event.element)
  }

  layers.forEach((layer) => {
    applyLayerHints(layer)
    watchSource(layer)
  })
  layers.on('add', onAdd as never)
  layers.on('remove', onRemove as never)

  return () => {
    layers.un('add', onAdd as never)
    layers.un('remove', onRemove as never)
    sourceWatchers.forEach((unwatch) => unwatch())
    sourceWatchers.clear()
  }
}
